import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useBackNavigationContext } from "@/contexts/BackNavigationContext";

/**
 * Returns back navigation state and a `goBack` function.
 * Used by the global header to render the back button.
 *
 * Smart behavior:
 * - If the user arrived via in-app navigation (history.state.idx > 0),
 *   goes back in browser history (like the browser back button).
 * - If the user opened the page directly (bookmark, URL bar), navigates
 *   to the fallback path registered by the detail page.
 */
export function useBackNavigation() {
  const { state, leaveGuard } = useBackNavigationContext();
  const navigate = useNavigate();

  const goBack = useCallback(() => {
    const leave = () => {
      // React Router stores a navigation index in window.history.state.idx.
      // idx === 0 means this is the first entry in the session (direct navigation).
      const historyIdx = (
        window.history.state as { idx?: number } | null
      )?.idx;

      if (historyIdx !== undefined && historyIdx > 0) {
        navigate(-1);
      } else if (state.fallbackPath) {
        navigate(state.fallbackPath);
      }
    };
    // A page with unsaved edits registers a guard; the shell's Back asks first.
    if (leaveGuard) {
      leaveGuard(leave);
    } else {
      leave();
    }
  }, [navigate, state.fallbackPath, leaveGuard]);

  return {
    isVisible: state.fallbackPath !== null,
    goBack,
  };
}
